import type { SalesforceContact } from 'src/types/salesforce-contact';
import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';

import type { HouseholdStage } from '@savvy/constants/generated/gql-enums';
import clientEnv from '@savvy/env/client';

import type { SalesforceAccount } from '../../types/salesforce-account';

export function findPrimaryContact(account: SalesforceAccount): SalesforceContact | undefined {
  if (!account.Contacts?.records) {
    return undefined;
  }
  const contacts = account.Contacts.records;
  const contact = contacts.find((contact) => contact.XLR8CS__Primary_Contact__c);
  return contact;
}

export function findCoClientContact(account: SalesforceAccount) {
  if (!account.Contacts?.records) {
    return undefined;
  }
  const contacts = account.Contacts.records;
  return contacts.find((contact) => contact.Co_Client__c);
}

export function lessThan15MinutesOld(account: SalesforceAccount): boolean {
  return DateTime.fromISO(account.CreatedDate).plus({ minutes: 15 }) > DateTime.now();
}

export const CLIENT_SERVICES_NAME = 'Savvy Client Services';
export const CLIENT_SERVICES_ACCOUNT_ID =
  clientEnv.NEXT_PUBLIC_VERCEL_ENV === 'development' ? '005Va000001075lIAA' : '0058c000009OexqAAC';
export const CLIENT_SERVICES_PROFILE_PIC_URL =
  'https://res.cloudinary.com/hbb0rvydd/image/upload/c_thumb,g_face,h_300,w_300/v1731699559/profile-pics/veni5wvvohly9m2mzreq.png';
export const INTEGRATION_USER_ID = '0058c000009LuyYAAS';

export function getUserNameFromId(id: string, account: SalesforceAccount) {
  if (id === CLIENT_SERVICES_ACCOUNT_ID) {
    return CLIENT_SERVICES_NAME;
  }
  if (id === INTEGRATION_USER_ID) {
    return 'Savvy Product Admin';
  }
  if (id === account.OwnerId) {
    return account.Owner?.Name || 'Advisor';
  }
  return 'Unknown';
}

export type HouseholdFragment = {
  readonly id: string;
  readonly name: string;
  readonly salesforceAccountId?: string | null;
  readonly householdStage?: HouseholdStage | null;
  readonly primaryClient?: {
    readonly id: string;
    readonly salesforceContactId?: string | null;
    readonly user?: {
      readonly id: string;
      readonly firstName?: string | null;
      readonly lastName?: string | null;
      readonly email?: string | null;
      readonly phoneNumber?: string | null;
      readonly profilePicUrl?: string | null;
    } | null;
  } | null;
  readonly coClient?: {
    readonly id: string;
    readonly salesforceContactId?: string | null;
    readonly user?: {
      readonly id: string;
      readonly firstName?: string | null;
      readonly lastName?: string | null;
      readonly email?: string | null;
      readonly phoneNumber?: string | null;
      readonly profilePicUrl?: string | null;
    } | null;
  } | null;
  readonly advisor?: {
    readonly id: string;
    readonly user?: {
      readonly id: string;
      readonly firstName?: string | null;
      readonly lastName?: string | null;
    } | null;
  } | null;
};

/**
 * Helper function to create a minimal SalesforceAccount-like object from Household data.
 * This is used to maintain compatibility with existing child components that expect
 * a SalesforceAccount structure.
 */
export function createAccountAdapter(
  household: HouseholdFragment,
  advisorName?: string | null,
  advisorSalesforceUserId?: string | null,
): SalesforceAccount {
  const contacts: {
    readonly Id: string;
    FirstName?: string;
    LastName?: string;
    Email?: string;
    Phone?: string;
    Co_Client__c: boolean;
    XLR8CS__Primary_Contact__c: boolean;
    Savvy_User_Id__c?: string;
    Profile_Pic_Url__c?: string;
  }[] = [];

  // Add primary client contact if available
  if (!isNil(household.primaryClient?.salesforceContactId) && household.primaryClient.salesforceContactId !== '') {
    contacts.push({
      Id: household.primaryClient.salesforceContactId,
      FirstName: household.primaryClient.user?.firstName ?? undefined,
      LastName: household.primaryClient.user?.lastName ?? undefined,
      Email: household.primaryClient.user?.email ?? undefined,
      Phone: household.primaryClient.user?.phoneNumber ?? undefined,
      Co_Client__c: false,
      XLR8CS__Primary_Contact__c: true,
      Savvy_User_Id__c: household.primaryClient.user?.id,
      Profile_Pic_Url__c: household.primaryClient.user?.profilePicUrl ?? undefined,
    });
  }

  // Add co-client contact if available
  if (!isNil(household.coClient?.salesforceContactId) && household.coClient.salesforceContactId !== '') {
    contacts.push({
      Id: household.coClient.salesforceContactId,
      FirstName: household.coClient.user?.firstName ?? undefined,
      LastName: household.coClient.user?.lastName ?? undefined,
      Email: household.coClient.user?.email ?? undefined,
      Phone: household.coClient.user?.phoneNumber ?? undefined,
      Co_Client__c: true,
      XLR8CS__Primary_Contact__c: false,
      Savvy_User_Id__c: household.coClient.user?.id,
      Profile_Pic_Url__c: household.coClient.user?.profilePicUrl ?? undefined,
    });
  }

  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    Id: household.salesforceAccountId!,
    Name: household.name,
    Savvy_User_Id__c: household.primaryClient?.user?.id,
    CreatedDate: new Date().toISOString(),
    Qualified_Client__c: false,
    Qualified_Purchaser__c: false,
    Accredited_Investor__c: false,
    Owner: {
      Name: advisorName ?? advisorNameFromFragment(household.advisor ?? undefined),
    },
    OwnerId: advisorSalesforceUserId ?? undefined,
    Contacts: {
      records: contacts,
    },
  };
}

function advisorNameFromFragment(advisor: HouseholdFragment['advisor']) {
  return !isNil(advisor?.user?.firstName) && !isNil(advisor.user.lastName)
    ? `${advisor.user.firstName} ${advisor.user.lastName}`
    : undefined;
}
